import { useMgQual, useMgQuant } from "../lib/realdata";

export default function Repository() {
  const quant = useMgQuant();
  const qual = useMgQual();

  const w1n = quant?.meta.waves[0]?.n_total ?? 1491;
  const w2n = quant?.meta.waves[1]?.n_total ?? 742;
  const total = (quant?.meta.total_respondents) ?? (w1n + w2n);
  const qualN = qual?.meta.total_interviews ?? 18;
  const qualCities = qual?.meta.cities.join(", ") ?? "Kolkata, Lucknow, Gorakpur";
  const w1Metric = quant?.brand_awareness_by_wave.wave1.metric;
  const w2Metric = quant?.brand_awareness_by_wave.wave2.metric;
  const metricsDiffer = w1Metric && w2Metric && w1Metric !== w2Metric;

  return (
    <div className="stagger">
      <div style={{ marginBottom: 16 }}>
        <h1 className="h-page">Data</h1>
        <div className="lead">What Pulse is reading from, and whether you can trust it.</div>
      </div>

      <div className="data-overview">
        <div className="healthmark">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
        <div>
          <div className="t">Your data is healthy.</div>
          <div className="s">
            2 waves of quant (<b>{total.toLocaleString()} respondents</b>: {w1n.toLocaleString()} in Wave 1, {w2n.toLocaleString()} in Wave 2), <b>{qualN} depth interviews</b> across {qualCities}, indexed and in sync.{" "}
            {metricsDiffer ? (
              <>
                Pulse flagged a schema drift between waves: Wave 1 used <b>{prettyMetric(w1Metric)}</b>, Wave 2 replaced it with <b>{prettyMetric(w2Metric)}</b>. Both are surfaced, not merged.
              </>
            ) : (
              <>No unresolved conflicts.</>
            )}
          </div>
        </div>
        <button className="btn-secondary">View full log</button>
      </div>

      <div className="data-grid">
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Sources</div>
          <div className="source-list">
            <div className="source-row">
              <div className="dot" />
              <div>
                <div className="t">Wave 1 · Quant</div>
                <div className="m">SPSS dictionary · KA_MG_Questionnaire_Final_Wave 1.docx · 3,490 variables</div>
              </div>
              <div className="n">n={w1n.toLocaleString()}</div>
              <div className="ts">Apr '24</div>
            </div>
            <div className="source-row">
              <div className="dot" />
              <div>
                <div className="t">Wave 2 · Quant</div>
                <div className="m">SPSS dictionary · KA_MG_Questionnaire_Final_Wave 2.docx · 3,517 variables</div>
              </div>
              <div className="n">n={w2n.toLocaleString()}</div>
              <div className="ts">Feb '26</div>
            </div>
            <div className="source-row">
              <div className="dot qa" />
              <div>
                <div className="t">Kolkata · Depth interviews</div>
                <div className="m">Moderator: Shalini · 6 DIs · avg 34 min</div>
              </div>
              <div className="n">n=6</div>
              <div className="ts">Mar '26</div>
            </div>
            <div className="source-row">
              <div className="dot qa" />
              <div>
                <div className="t">Lucknow · Depth interviews</div>
                <div className="m">Moderator: Blessington · 6 DIs · avg 38 min</div>
              </div>
              <div className="n">n=6</div>
              <div className="ts">Mar '26</div>
            </div>
            <div className="source-row">
              <div className="dot qa" />
              <div>
                <div className="t">Gorakpur · Depth interviews</div>
                <div className="m">Moderator: Vaibhav · 6 DIs · avg 41 min</div>
              </div>
              <div className="n">n=6</div>
              <div className="ts">Mar '26</div>
            </div>
            <div className="source-row">
              <div className="dot sc" />
              <div>
                <div className="t">Amazon India</div>
                <div className="m">Hourly pull · Preethi, Bajaj, Philips, Sujata, Usha</div>
              </div>
              <div className="n">41.2K</div>
              <div className="ts">live</div>
            </div>
            <div className="source-row">
              <div className="dot sc" />
              <div>
                <div className="t">Flipkart</div>
                <div className="m">Hourly pull · 5 brands, top 12 SKUs</div>
              </div>
              <div className="n">18.6K</div>
              <div className="ts">live</div>
            </div>
            <div className="source-row">
              <div className="dot sc" />
              <div>
                <div className="t">Twitter / X</div>
                <div className="m">Streaming · brand mentions + hashtags</div>
              </div>
              <div className="n">7.4K</div>
              <div className="ts">live</div>
            </div>
            <div className="source-row">
              <div className="dot sc" />
              <div>
                <div className="t">YouTube comments</div>
                <div className="m">Daily pull · review videos + unboxings</div>
              </div>
              <div className="n">4.1K</div>
              <div className="ts">daily</div>
            </div>
          </div>
        </div>

        <div>
          <div className="coverage-panel" style={{ marginBottom: 14 }}>
            <h3>Coverage by theme</h3>
            <div className="coverage-row">
              <b>Brand salience</b>
              <div className="prog"><span style={{ width: "92%" }} /></div>
              <span className="val">92%</span>
            </div>
            <div className="coverage-row">
              <b>Brand imagery</b>
              <div className="prog"><span style={{ width: "88%" }} /></div>
              <span className="val">88%</span>
            </div>
            <div className="coverage-row">
              <b>NPS &amp; satisfaction</b>
              <div className="prog"><span style={{ width: "100%" }} /></div>
              <span className="val">100%</span>
            </div>
            <div className="coverage-row">
              <b>Durability / pain</b>
              <div className="prog"><span style={{ width: "76%", background: "var(--amber)" }} /></div>
              <span className="val">76%</span>
            </div>
            <div className="coverage-row">
              <b>Psychographics</b>
              <div className="prog"><span style={{ width: "84%" }} /></div>
              <span className="val">84%</span>
            </div>
            <div className="coverage-row">
              <b>Qual narrative</b>
              <div className="prog"><span style={{ width: "100%", background: "var(--qual)" }} /></div>
              <span className="val">3 cities</span>
            </div>
          </div>

          <div className="card" style={{ padding: "18px 20px" }}>
            <h3 style={{ fontSize: 14.5, marginBottom: 10 }}>Recent harmonisations</h3>
            <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
              <div style={{ padding: "8px 0", borderBottom: "1px solid var(--line-2)" }}>
                <div style={{ fontWeight: 500 }}>"Preethi Kitchen Appliances Pvt Ltd" → "Preethi"</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Confidence 1.00 · Auditor approved (AK, Apr 18)</div>
              </div>
              <div style={{ padding: "8px 0", borderBottom: "1px solid var(--line-2)" }}>
                <div style={{ fontWeight: 500 }}>36 other brand-code consolidations</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Avg confidence 0.97 · 2 required auditor review</div>
              </div>
              <div style={{ padding: "8px 0", borderBottom: "1px solid var(--line-2)" }}>
                <div style={{ fontWeight: 500 }}>11 question-label drifts between waves</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Reconciled to the Wave 2 wording · downstream charts updated</div>
              </div>
              <div style={{ padding: "8px 0" }}>
                <div style={{ fontWeight: 500 }}>Kolkata DI 3 (Bengali) · auto-translated</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Original transcript retained · human review complete</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function prettyMetric(m: string | null | undefined): string {
  if (m === "total_aware") return "Total brand awareness (MQ7)";
  if (m === "ever_used") return "Brands ever used (MQ9)";
  if (m === "currently_using") return "Brands currently used (MQ10)";
  return m ?? "—";
}
